import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Retro Zork-like engine in one file
// - Node-based state machine
// - Choice buttons + quick-select keys 1-9
// - Minimal parser (look, inventory, take X, go N/S/E/W)
// - Inventory & flags
// - TTS via Web Speech API (retro-ish voice settings)
// - Autosave to localStorage
// - Green-on-black CRT vibes with Tailwind

// -----------------------------
// 1) GAME CONTENT (edit me)
// -----------------------------
// A tiny starter map to show the structure. Add more rooms/nodes and choices.
const GAME_NODES = {
  intro: {
    title: "A Dark Room",
    text: (
      "You awaken in darkness. A soft humming fills the air — where is it coming from? " +
      "Your eyes adjust: a faint glow leaks under a door to the north."
    ),
    choices: [
      { id: "c1", label: "Go north", to: "hall" },
      { id: "c2", label: "Listen carefully", to: "hum", effect: { setFlag: ["heard_hum", true] } },
    ],
  },
  hum: {
    title: "The Humming",
    text:
      "You press your ear to the wall. The hum is mechanical — rhythmic, patient. Perhaps a generator?",
    choices: [
      { id: "back", label: "Back", to: "intro" },
    ],
  },
  hall: {
    title: "North Hallway",
    text:
      "A narrow hall stretches east and west. To the north, a heavy door with a keyhole. A note lies on the floor.",
    choices: [
      { id: "east", label: "Go east", to: "east" },
      { id: "west", label: "Go west", to: "west" },
      { id: "read_note", label: "Read the note", to: "note", effect: { addItem: "note" } },
      {
        id: "north",
        label: "Try the heavy door",
        to: (state) => (state.inventory.includes("brass key") ? "generator" : "locked"),
      },
    ],
  },
  note: {
    title: "A Crumpled Note",
    text:
      "The note reads: 'Left is leisure, right is duty. The key waits where the air is warm.'",
    choices: [{ id: "back", label: "Back", to: "hall" }],
  },
  east: {
    title: "East Passage",
    text:
      "Pipes line the walls. The air grows warmer here. Something metallic glints in the dust.",
    choices: [
      {
        id: "take_key",
        label: "Pick up the brass key",
        to: "east",
        show: (s) => !s.inventory.includes("brass key"),
        effect: { addItem: "brass key" },
      },
      { id: "back", label: "Back to hall", to: "hall" },
    ],
  },
  west: {
    title: "West Passage",
    text:
      "Old posters peel from the walls. A cracked vending machine wheezes. This side is cooler, quieter.",
    choices: [{ id: "back", label: "Back to hall", to: "hall" }],
  },
  locked: {
    title: "Locked Door",
    text:
      "You rattle the handle. Locked. A key would help.",
    choices: [{ id: "back", label: "Back", to: "hall" }],
  },
  generator: {
    title: "Generator Room",
    text:
      "A squat generator thrums with steady power. Consoles blink. There might be a way to restore lights to the complex.",
    choices: [
      {
        id: "lights",
        label: "Flip the main breaker",
        to: "lit",
        effect: { setFlag: ["lights_on", true] },
        show: (s) => !s.flags.lights_on,
      },
      { id: "back", label: "Back to hall", to: "hall" },
    ],
  },
  lit: {
    title: "Lights On",
    text:
      "With a thunderous clack, overhead lights flare to life. Dust dances in the sudden brightness.",
    choices: [{ id: "back", label: "Back", to: "hall" }],
  },
};

// -----------------------------
// 2) ENGINE
// -----------------------------
const DEFAULT_STATE = {
  node: "intro",
  inventory: [],
  flags: {},
  log: ["You feel… awake?"],
  tts: true,
};

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function speak(text, enabled = true) {
  if (!enabled) return;
  try {
    const u = new SpeechSynthesisUtterance(text);
    // Retro-ish robotic vibe
    u.rate = 0.9;
    u.pitch = 0.6;
    u.volume = 1.0;
    // Try to select a monotone/English voice if available
    const voices = window.speechSynthesis.getVoices();
    const en = voices.find((v) => /en/i.test(v.lang) && /male|google british|fred|zarvox|alex|dan|samantha/i.test(v.name)) || voices[0];
    if (en) u.voice = en;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch {}
}

function applyEffect(state, effect) {
  if (!effect) return state;
  const next = { ...state, inventory: [...state.inventory], flags: { ...state.flags } };
  if (effect.addItem && !next.inventory.includes(effect.addItem)) next.inventory.push(effect.addItem);
  if (effect.removeItem) next.inventory = next.inventory.filter((i) => i !== effect.removeItem);
  if (effect.setFlag) {
    const [key, val] = effect.setFlag;
    next.flags[key] = val;
  }
  if (effect.log) next.log = [...next.log, effect.log];
  return next;
}

function resolveTo(choiceTo, state) {
  if (typeof choiceTo === "function") return choiceTo(state);
  return choiceTo;
}

export default function Schmorg() {
  const [state, setState] = useState(() => {
    const saved = localStorage.getItem("schmorg_save");
    return saved ? JSON.parse(saved) : DEFAULT_STATE;
  });

  const current = GAME_NODES[state.node] || GAME_NODES.intro;
  const [input, setInput] = useState("");
  const logRef = useRef(null);

  // Persist
  useEffect(() => {
    localStorage.setItem("schmorg_save", JSON.stringify(state));
  }, [state]);

  // TTS on node change
  useEffect(() => {
    const text = ` ${current.title}. ${current.text}`;
    speak(text, state.tts);
  }, [state.node, state.tts, current.text, current.title]);

  // Keep log scrolled
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [state.log, state.node]);

  const visibleChoices = useMemo(() => {
    return (current.choices || []).filter((c) => (c.show ? c.show(state) : true));
  }, [current, state]);

  // Choice click/keys
  const choose = useCallback((choice) => {
    setState((prev) => {
      const base = applyEffect(prev, choice.effect);
      const go = resolveTo(choice.to, base);
      const nextNode = GAME_NODES[go] ? go : prev.node;
      return { ...base, node: nextNode, log: [...base.log, `> ${choice.label}`] };
    });
  }, []);

  function doCommand(cmdRaw) {
    const cmd = cmdRaw.trim().toLowerCase();
    if (!cmd) return;

    // Built-ins
    if (["help", "?"].includes(cmd)) {
      return appendLog(
        "Commands: look, inventory/inv, take <item>, go <n|s|e|w|north|south|east|west>, save, load, mute, unmute"
      );
    }
    if (["inventory", "inv", "i"].includes(cmd)) {
      const inv = state.inventory.length ? state.inventory.join(", ") : "(empty)";
      return appendLog("Inventory: " + inv);
    }
    if (cmd === "look" || cmd === "l") {
      appendLog(`${current.title}: ${current.text}`);
      speak(`${current.title}. ${current.text}`, state.tts);
      return;
    }
    if (cmd === "save") {
      localStorage.setItem("schmorg_save", JSON.stringify(state));
      return appendLog("Game saved.");
    }
    if (cmd === "load") {
      const saved = localStorage.getItem("schmorg_save");
      if (saved) setState(JSON.parse(saved));
      return appendLog("Loaded.");
    }
    if (cmd === "mute") {
      setState((s) => ({ ...s, tts: false }));
      return appendLog("TTS muted.");
    }
    if (cmd === "unmute") {
      setState((s) => ({ ...s, tts: true }));
      return appendLog("TTS on.");
    }

    // Simple parser helpers
    const GO = /^go\s+(n|s|e|w|north|south|east|west)$/;
    const TAKE = /^take\s+(.+)$/;

    if (GO.test(cmd)) {
      const dir = cmd.split(/\s+/)[1];
      const dirMap = { n: "north", s: "south", e: "east", w: "west" };
      const canonical = dirMap[dir] || dir;

      // Try to find a matching choice label that contains the direction word
      const found = visibleChoices.find((c) => c.label.toLowerCase().includes(canonical));
      if (found) return choose(found);
      return appendLog("You can’t go that way.");
    }

    if (TAKE.test(cmd)) {
      const item = cmd.replace(TAKE, "$1");
      // Try to find a choice that adds this item
      const getChoice = visibleChoices.find(
        (c) => c.effect?.addItem && c.effect.addItem.toLowerCase() === item
      );
      if (getChoice) return choose(getChoice);
      return appendLog("You don’t see that here.");
    }

    appendLog("(no effect)");
  }

  function appendLog(line) {
    setState((s) => ({ ...s, log: [...s.log, line] }));
  }

  // Keyboard: 1-9 select choice
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")) return;
      const n = parseInt(e.key, 10);
      if (!isNaN(n)) {
        const idx = clamp(n - 1, 0, visibleChoices.length - 1);
        const choice = visibleChoices[idx];
        if (choice) {
          e.preventDefault();
          choose(choice);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visibleChoices, choose]);

  // UI
  return (
    <div className="min-h-screen bg-black text-green-400 font-mono">
      <div className="max-w-3xl mx-auto p-4">
        <header className="flex items-center justify-between mb-3">
          <h1 className="text-xl tracking-widest">schmorg // v0.1</h1>
          <div className="space-x-2">
            <button
              className="px-2 py-1 rounded border border-green-600 hover:bg-green-900/30"
              onClick={() => setState(DEFAULT_STATE)}
            >
              New Game
            </button>
            <button
              className="px-2 py-1 rounded border border-green-600 hover:bg-green-900/30"
              onClick={() => setState((s) => ({ ...s, tts: !s.tts }))}
            >
              {state.tts ? "Mute TTS" : "Unmute TTS"}
            </button>
          </div>
        </header>

        <main className="grid gap-4 md:grid-cols-[1fr_18rem]">
          <section className="rounded-lg border border-green-800 bg-green-950/20 p-3 shadow-[0_0_12px_rgba(0,255,150,.2)]">
            <div className="text-green-300 text-sm mb-2 opacity-80">{current.title}</div>
            <div className="whitespace-pre-line leading-relaxed">{current.text}</div>

            <ol className="mt-4 grid gap-2">
              {visibleChoices.map((c, i) => (
                <li key={c.id}>
                  <button
                    onClick={() => choose(c)}
                    className="w-full text-left px-3 py-2 rounded border border-green-700 hover:bg-green-900/40"
                  >
                    <span className="text-green-500 mr-2">[{i + 1}]</span>
                    {c.label}
                  </button>
                </li>
              ))}
            </ol>

            <form
              className="mt-4 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                doCommand(input);
                setInput("");
              }}
            >
              <input
                className="flex-1 bg-black/60 border border-green-800 rounded px-3 py-2 outline-none focus:border-green-400"
                placeholder="Type a command (help, look, inv, go north, take key, save, load, mute)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                spellCheck={false}
              />
              <button className="px-3 py-2 rounded border border-green-700 hover:bg-green-900/40">
                Enter
              </button>
            </form>
          </section>

          <aside className="rounded-lg border border-green-800 bg-green-950/10 p-3">
            <div className="text-green-300 text-sm mb-2 opacity-80">Log</div>
            <div ref={logRef} className="h-56 overflow-auto pr-2 space-y-1">
              {state.log.map((l, i) => (
                <div key={i} className="opacity-90">
                  {l}
                </div>
              ))}
            </div>

            <div className="text-green-300 text-sm mt-4 mb-2 opacity-80">Inventory</div>
            <ul className="list-disc list-inside space-y-1">
              {state.inventory.length === 0 ? (
                <li className="opacity-60">(empty)</li>
              ) : (
                state.inventory.map((it) => <li key={it}>{it}</li>)
              )}
            </ul>

            <div className="text-green-300 text-sm mt-4 mb-2 opacity-80">Flags</div>
            <div className="text-xs break-words opacity-80">
              {JSON.stringify(state.flags)}
            </div>
          </aside>
        </main>

        <footer className="mt-6 text-xs opacity-60">
          <p>
            Quick keys: 1-9 for choices • Commands: <code>help</code>, <code>look</code>, <code>inv</code>,
            <code>take &lt;item&gt;</code>, <code>go north/south/east/west</code>, <code>save</code>, <code>load</code>,
            <code>mute</code>, <code>unmute</code>
          </p>
        </footer>
      </div>

      {/* CRT scanline & vignette */}
      <div className="pointer-events-none fixed inset-0 mix-blend-soft-light opacity-40"
           style={{ backgroundImage: "repeating-linear-gradient(0deg, rgba(0,0,0,.0) 0, rgba(0,0,0,.05) 2px, transparent 2px, transparent 4px)" }}
      />
      <div className="pointer-events-none fixed inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />
    </div>
  );
}

// -----------------------------
// 4) HOW TO EXTEND
// -----------------------------
// • Add rooms: add entries to GAME_NODES.
// • Each node has { title, text, choices[] }.
// • A choice: { id, label, to, show?, effect? }
//    - to: string id of next node OR (state)=>id for conditional routing
//    - show(state): return false to hide until a condition is met
//    - effect: { addItem?: string, removeItem?: string, setFlag?: [key, value], log?: string }
// • TTS: uses Web Speech API. For a closer C64 SAM vibe you can later wire up a library
//   (e.g. a JS SAM port) and call it inside speak().
// • Save/Load: localStorage key "schmorg_save".
// • Styling: Tailwind utility classes.
